import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { studioApi, studioMock, idempotencyKey } from '../../../../../data/studioApi';
import { STUDIO_AUTOMATIONS_LIST } from '../../../../../enums/routes';

// One automation with its results (GET /automations/{id} + /insights), and what can be done to it:
// pause, activate again, send to the trash. `gate` says why an activation was refused.
export default function useAutomationDetail(s, id) {
  const { t } = useTranslation('studioAutomations');
  const [automation, setAutomation] = useState(null);
  const [insights, setInsights] = useState(null);
  const [media, setMedia] = useState([]);
  const [status, setStatus] = useState('loading');
  const [busy, setBusy] = useState(null);
  const [gate, setGate] = useState(null);
  const live = useRef(true);
  useEffect(() => () => { live.current = false; }, []);

  const load = useCallback(async () => {
    try {
      const [read, results] = await Promise.all([studioApi.automation(id), studioApi.automationInsights(id)]);
      if (!live.current) return;
      setAutomation(read);
      setInsights(results);
      setStatus('ready');
      const items = await Promise.all(read.trigger.media_ids.map(mediaId => studioApi.mediaItem(mediaId).catch(() => null)));
      if (live.current) setMedia(items.filter(Boolean));
    } catch (failure) { if (live.current) setStatus(failure.code === 'not_found' ? 'missing' : 'error'); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const change = useCallback(async (kind, body) => {
    setBusy(kind);
    try {
      const next = await studioApi.updateAutomation(id, { expected_version: automation.version, ...body }, idempotencyKey('automation-status'));
      if (live.current) setAutomation(next);
      s.automations.refresh();
      return next;
    } catch (failure) {
      if (failure.code === 'plan_upgrade_required') s.automations.upgrade(s.automations.planForFields(failure.errors || {}));
      else if (failure.code === 'automation_limit_reached') setGate('limit');
      else if (failure.code === 'connection_required') setGate('connection');
      else if (failure.code === 'keyword_conflict' || failure.code === 'validation_error') setGate({ fields: Object.values(failure.errors || {}).flat() });
      else if (failure.code === 'version_conflict') load();
      else s.act(() => { throw failure; });
      return null;
    } finally { if (live.current) setBusy(null); }
  }, [automation, id, load, s]);
  const pause = useCallback(() => change('pause', { status: 'paused' }), [change]);
  const resume = useCallback(() => change('resume', { status: 'active' }), [change]);
  const trash = useCallback(async () => {
    setBusy('trash');
    const done = await s.act(() => studioApi.deleteAutomation(id, idempotencyKey('automation-trash')));
    if (!live.current) return;
    setBusy(null);
    if (done) {
      s.automations.refresh();
      window.setSnackbar?.(t('trashed', { name: automation.name }));
      s.navigate(STUDIO_AUTOMATIONS_LIST);
    }
  }, [automation, id, s, t]);
  // `?mock=true` only: someone comments the keyword, so the numbers and the inbox move.
  const simulate = useCallback(async () => {
    setBusy('simulate');
    try {
      await studioMock.simulateTrigger(id);
      await load();
      s.automations.refresh();
    } finally { if (live.current) setBusy(null); }
  }, [id, load, s.automations]);

  return { status, automation, insights, media, busy, gate, setGate, reload: load, pause, resume, trash, simulate: studioMock ? simulate : null };
}
